import React, { useRef, useEffect, useState } from 'react';

const DocumentRightToc = ({ data, currentSlug, activeId, trendingTopic }) => {
  const itemRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  // Get section data (for normal docs)
  const section = data && data.length > 0 ? data[0] : null;

  // Update indicator position when activeId changes (normal docs)
  useEffect(() => {
    if (activeId && section?.subSections) {
      const activeIndex = section.subSections.findIndex(
        (sub) => sub.title.toLowerCase().replace(/\s+/g, '-') === activeId
      );
      if (activeIndex !== -1 && itemRefs.current[activeIndex]) {
        const element = itemRefs.current[activeIndex];
        setIndicatorStyle({
          top: element.offsetTop,
          height: element.offsetHeight,
        });
      }
    }
  }, [activeId, section]);

  // Update indicator for trending steps
  useEffect(() => {
    if (!activeId || !trendingTopic) return;
    const timer = setTimeout(() => {
      const activeIndex = trendingTopic.steps?.findIndex(
        (step, i) => `trending-step-${i}` === activeId
      );
      if (activeIndex !== -1 && itemRefs.current[activeIndex]) {
        const element = itemRefs.current[activeIndex];
        setIndicatorStyle({
          top: element.offsetTop,
          height: element.offsetHeight,
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [activeId, trendingTopic]);

  const handleClick = (id) => {
    const element = document.getElementById(id);
    const contentContainer = document.querySelector(
      '.flex-1.overflow-y-auto.scrollbar'
    );
    if (element && contentContainer) {
      const elementRect = element.getBoundingClientRect();
      const containerRect = contentContainer.getBoundingClientRect();
      const scrollOffset =
        elementRect.top - containerRect.top + contentContainer.scrollTop - 100;
      contentContainer.scrollTo({ top: scrollOffset, behavior: 'smooth' });
    }
  };

  // ── TRENDING TOPIC TOC ──
  if (trendingTopic) {
    return (
      <div className="sticky top-6">
        <div className="text-[#D8D8D8] font-semibold text-lg mb-4 pb-2 border-b border-[#515151]">
          On This Page
        </div>
        <nav className="space-y-2">
          {/* Topic title as header */}
          <button
            onClick={() => handleClick(`trending-step-0`)}
            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200
    ${
      activeId === 'trending-step-0'
        ? 'text-[#EE2B2A] bg-[#EE2B2A]/10 border-l-2 border-[#EE2B2A] font-medium'
        : 'text-[#C1C1C1] hover:text-white hover:bg-[#2A2A2A]'
    }`}
          >
            {trendingTopic.title}
          </button>
          {/* Steps as TOC items */}
          <div className="ml-2 relative">
            {/* Vertical line */}
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#515151]" />

            {/* Active indicator */}
            {activeId &&
              trendingTopic.steps?.some(
                (_, i) => `trending-step-${i}` === activeId
              ) && (
                <div
                  className="absolute left-0 w-[2px] bg-[#EE2B2A] transition-all duration-300 ease-out"
                  style={{
                    top: `${indicatorStyle.top}px`,
                    height: `${indicatorStyle.height}px`,
                  }}
                />
              )}

            <div className="pl-3 space-y-1">
              {trendingTopic.steps?.map((step, i) => {
                const stepId = `trending-step-${i}`;
                const isActive = activeId === stepId;
                return (
                  <button
                    key={i}
                    ref={(el) => (itemRefs.current[i] = el)}
                    onClick={() => handleClick(stepId)}
                    className={`block w-full text-left px-3 py-1.5 rounded text-sm transition-all duration-200
                      ${
                        isActive
                          ? 'text-[#EE2B2A] font-medium'
                          : 'text-[#949494] hover:text-white'
                      }`}
                  >
                    {step.step}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      </div>
    );
  }

  // ── NORMAL DOCS TOC ──
  if (!section) return null;

  return (
    <div className="sticky top-6">
      <div className="text-[#D8D8D8] font-semibold text-lg mb-4 pb-2 border-b border-[#515151]">
        On This Page
      </div>

      <nav className="space-y-2">
        {/* Main section link */}
        {section.sectionTitle && (
          <button
            onClick={() => handleClick(section.slug)}
            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200
              ${
                activeId === section.slug
                  ? 'text-[#EE2B2A] bg-[#EE2B2A]/10 border-l-2 border-[#EE2B2A] font-medium'
                  : 'text-[#C1C1C1] hover:text-white hover:bg-[#2A2A2A]'
              }`}
          >
            {section.sectionTitle}
          </button>
        )}

        {/* Subsection links */}
        {section.subSections && section.subSections.length > 0 && (
          <div className="ml-2 relative">
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#515151]" />

            {activeId &&
              section.subSections.some(
                (sub) =>
                  sub.title.toLowerCase().replace(/\s+/g, '-') === activeId
              ) && (
                <div
                  className="absolute left-0 w-[2px] bg-[#EE2B2A] transition-all duration-300 ease-out"
                  style={{
                    top: `${indicatorStyle.top}px`,
                    height: `${indicatorStyle.height}px`,
                  }}
                />
              )}

            <div className="pl-3 space-y-1">
              {section.subSections.map((sub, index) => {
                const subId = sub.title.toLowerCase().replace(/\s+/g, '-');
                return (
                  <button
                    key={index}
                    ref={(el) => (itemRefs.current[index] = el)}
                    onClick={() => handleClick(subId)}
                    className={`block w-full text-left px-3 py-1.5 rounded text-sm transition-all duration-200
                      ${
                        activeId === subId
                          ? 'text-[#EE2B2A] font-medium'
                          : 'text-[#949494] hover:text-white'
                      }`}
                  >
                    {sub.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default DocumentRightToc;
